import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Building2, Wrench, PlusCircle, GitBranch, Store, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import NewProjectForm from "@/components/project-forms/NewProjectForm";
import VariationOrderForm from "@/components/project-forms/VariationOrderForm";
import NewOutletForm from "@/components/project-forms/NewOutletForm";
import WorkingOrderForm from "@/components/project-forms/WorkingOrderForm";

/**
 * Orchestrator dialog untuk create Active Project.
 * 3-step flow: pick category → pick sub-type → isi form.
 * Setiap sub-form dipisah ke `/components/project-forms/*` untuk maintainability.
 */
export default function ActiveProjectDialog({ open, onOpenChange, onCreated, presetCategory = null, presetType = null }) {
  const [category, setCategory] = useState(null); // null | "general" | "maintenance"
  const [type, setType] = useState("new_project"); // new_project | variation_order | new_outlet | working_order
  const [parents, setParents] = useState([]);

  useEffect(() => {
    if (!open) return;
    // 2026-09-29 (user request): preset men-skip pemilih kategori — dipakai
    // tombol "+ New Working Order" / "+ New Outlet" di tab Monitoring WO.
    setCategory(presetCategory);
    setType(presetType || "new_project");
    api.get("/active-projects").then(({ data }) => {
      setParents(data.filter((p) => p.project_type === "new_project"));
    }).catch(() => {});
  }, [open]);

  const generalParents = useMemo(() => parents.filter((p) => !p.is_outlet), [parents]);
  const outletParents = useMemo(() => parents.filter((p) => p.is_outlet), [parents]);

  const goBack = () => setCategory(null);
  const chooseCategory = (cat) => {
    setCategory(cat);
    setType(cat === "general" ? "new_project" : "new_outlet");
  };
  const onDone = () => { onOpenChange(false); onCreated?.(); };
  const onCancel = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-xl max-h-[92vh] overflow-y-auto" data-testid="dialog-add-project">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight inline-flex items-center gap-2">
            {category && !presetCategory && (
              <button type="button" data-testid="cat-back" onClick={goBack}
                className="text-slate-500 hover:text-[#FF5A1F] transition-colors" title="Kembali ke pilihan kategori">
                <ArrowLeft size={18} />
              </button>
            )}
            Create Active Project
            {category === "general" && (
              <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5" data-testid="cat-badge-general">General</span>
            )}
            {category === "maintenance" && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5" data-testid="cat-badge-maintenance">Maintenance</span>
            )}
          </DialogTitle>
          <DialogDescription>
            {category === null
              ? "Pilih kategori project untuk memulai. Akses membuat project diatur via Access Control Matrix (fitur Projects → Create)."
              : category === "general"
              ? "Kontrak baru atau tambahan (Variation Order) pada project kontrak yang sudah ada."
              : "Struktur outlet maintenance + working order untuk pekerjaan berkala per outlet."}
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1 — Category picker */}
        {category === null && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2" data-testid="category-picker">
            <button type="button" data-testid="cat-general" onClick={() => chooseCategory("general")}
              className="group text-left p-4 border border-slate-300 hover:border-[#0B1D3A] hover:bg-slate-50 transition-all">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <Building2 size={20} />
                </div>
                <div>
                  <div className="font-display text-base text-[#0B1D3A] tracking-tight">General</div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    New Project (kontrak baru) atau Variation Order (tambahan pada project yang sudah ada).
                  </p>
                </div>
              </div>
            </button>
            <button type="button" data-testid="cat-maintenance" onClick={() => chooseCategory("maintenance")}
              className="group text-left p-4 border border-slate-300 hover:border-[#0B1D3A] hover:bg-slate-50 transition-all">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 text-amber-700 flex items-center justify-center shrink-0 group-hover:bg-amber-600 group-hover:text-white transition-colors">
                  <Wrench size={20} />
                </div>
                <div>
                  <div className="font-display text-base text-[#0B1D3A] tracking-tight">Maintenance</div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                    New Outlet (umbrella parent) atau New Working Order (per pekerjaan).
                  </p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* STEP 2 — Sub-type toggle */}
        {category === "general" && (
          <div className="inline-flex border border-slate-300 text-xs" data-testid="project-type-toggle">
            <button data-testid="type-new-project" onClick={() => setType("new_project")}
              className={`px-3 py-2 uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 ${type === "new_project" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <PlusCircle size={12} /> New Project
            </button>
            <button data-testid="type-variation-order" onClick={() => setType("variation_order")}
              className={`px-3 py-2 uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 ${type === "variation_order" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <GitBranch size={12} /> Variation Order
            </button>
          </div>
        )}
        {category === "maintenance" && (
          <div className="inline-flex border border-slate-300 text-xs" data-testid="project-type-toggle">
            <button data-testid="type-new-outlet" onClick={() => setType("new_outlet")}
              className={`px-3 py-2 uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 ${type === "new_outlet" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Store size={12} /> New Outlet
            </button>
            <button data-testid="type-working-order" onClick={() => setType("working_order")}
              className={`px-3 py-2 uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 ${type === "working_order" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <FileText size={12} /> New Working Order
            </button>
          </div>
        )}

        {/* STEP 3 — Forms (each is a self-contained component with own state + submit) */}
        {category === "general" && type === "new_project" && (
          <NewProjectForm parents={parents} onDone={onDone} onCancel={onCancel} />
        )}
        {category === "general" && type === "variation_order" && (
          <VariationOrderForm generalParents={generalParents} onDone={onDone} onCancel={onCancel} />
        )}
        {category === "maintenance" && type === "new_outlet" && (
          <NewOutletForm parents={parents} onDone={onDone} onCancel={onCancel} />
        )}
        {category === "maintenance" && type === "working_order" && (
          <WorkingOrderForm outletParents={outletParents} onDone={onDone} onCancel={onCancel} />
        )}
      </DialogContent>
    </Dialog>
  );
}
